/**
 * DashboardPage — KPIs por comercial.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, Paper, Stack, TextField,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import {
  API_BASE, getEmpresaId, getAuthHeaders, formatPrecio,
} from './format';

function fmtDateInput(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function DashboardPage() {
  const hoy = new Date();
  const hace30 = new Date();
  hace30.setDate(hace30.getDate() - 30);
  const [desde, setDesde] = useState(fmtDateInput(hace30));
  const [hasta, setHasta] = useState(fmtDateInput(hoy));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ empresa_id: getEmpresaId(), desde, hasta });
      const r = await fetch(`${API_BASE}/turismo/comercial/dashboard?${params}`,
        { headers: getAuthHeaders() });
      const d = await r.json();
      setData(d);
    } catch { setData(null); }
    setLoading(false);
  }, [desde, hasta]);
  useEffect(() => { cargar(); }, [cargar]);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Dashboard comercial</Typography>
        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
          Visitas, conversión y comisiones generadas por cada comercial.
        </Typography>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }}
          value={desde} onChange={e => setDesde(e.target.value)} />
        <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }}
          value={hasta} onChange={e => setHasta(e.target.value)} />
      </Stack>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress sx={{ color: '#f59e0b' }} /></Box>
      ) : !data || data.comerciales.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <DashboardIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            No hay comerciales registrados en esta empresa
          </Typography>
        </Paper>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 1.5,
        }}>
          {data.comerciales.map(c => (
            <Paper key={c.comercial_id} sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
                {c.comercial_nombre}
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: '#94a3b8', mb: 1.5 }}>
                {c.comercial_email}
              </Typography>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 0.75,
              }}>
                <Stat label="Visitas" value={c.visitas_total} color="#0d9488" />
                <Stat label="Positivas" value={c.visitas_positivas} sub={`${c.tasa_conversion}%`} color="#16a34a" />
                <Stat label="Agencias" value={c.agencias_asignadas} color="#0ea5e9" />
                <Stat label="Prospectos abiertos" value={c.prospectos_abiertos} color="#8b5cf6" />
                <Stat label="Ganados periodo" value={c.prospectos_ganados_periodo} color="#16a34a" />
                <Stat label="Pendientes hoy" value={c.pendientes_seguimiento}
                  color={c.pendientes_seguimiento > 0 ? '#dc2626' : '#94a3b8'} />
              </Box>

              <Box sx={{ mt: 1.5, p: 1, borderRadius: 1.5, bgcolor: '#f8fafc' }}>
                <Typography sx={{ fontSize: 10, color: '#94a3b8',
                                   textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.3 }}>
                  Comisiones generadas
                </Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#f59e0b' }}>
                  {formatPrecio(c.comisiones_generadas)}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}

function Stat({ label, value, color, sub }) {
  return (
    <Box sx={{ p: 0.75, borderRadius: 1, bgcolor: '#fff', borderLeft: `3px solid ${color}` }}>
      <Typography sx={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase',
                          fontWeight: 700, lineHeight: 1.1 }}>
        {label}
      </Typography>
      <Stack direction="row" alignItems="baseline" spacing={0.5}>
        <Typography sx={{ fontSize: 16, fontWeight: 800, color }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: 10, color: '#64748b' }}>{sub}</Typography>}
      </Stack>
    </Box>
  );
}
